// src/components/ProjectManagement/ExtendTaskDateModal.jsx
import React, { useState } from 'react';
import { db } from '../../firebase/config';
import { doc, updateDoc, addDoc, collection } from 'firebase/firestore';

const ExtendTaskDateModal = ({ task, onClose, onSuccess, currentUser }) => {
  // Helper function to safely get date value
  const getDateValue = (dateField) => {
    if (!dateField) return '';
    if (dateField?.toDate) {
      return dateField.toDate().toISOString().split('T')[0];
    }
    if (dateField instanceof Date) {
      return dateField.toISOString().split('T')[0];
    }
    if (typeof dateField === 'string') {
      return new Date(dateField).toISOString().split('T')[0];
    }
    return '';
  };

  // Helper function to format date for display
  const formatDateDisplay = (dateField) => {
    if (!dateField) return 'Not set';
    if (dateField?.toDate) {
      return dateField.toDate().toLocaleDateString();
    }
    if (dateField instanceof Date) {
      return dateField.toLocaleDateString();
    }
    if (typeof dateField === 'string') {
      return new Date(dateField).toLocaleDateString();
    }
    return 'Not set';
  };

  const [newStartDate, setNewStartDate] = useState(getDateValue(task.startDate));
  const [newEndDate, setNewEndDate] = useState(getDateValue(task.endDate));
  const [reason, setReason] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async () => {
    // Validate dates
    if (!newStartDate && !newEndDate) {
      alert('Please select at least one date to update');
      return;
    }

    if (newStartDate && newEndDate) {
      const start = new Date(newStartDate);
      const end = new Date(newEndDate);
      if (start > end) {
        alert('Start date cannot be after end date');
        return;
      }
    }

    if (!reason.trim()) {
      alert('Please provide a reason for changing the task dates');
      return;
    }

    setIsLoading(true);
    try {
      const updates = {};
      let hasChanges = false;
      let dateChangeMessage = '';

      // Handle start date update
      if (newStartDate && getDateValue(task.startDate) !== newStartDate) {
        const startDateObj = new Date(newStartDate);
        updates.startDate = startDateObj;
        dateChangeMessage += `\n• Start date: ${formatDateDisplay(task.startDate)} → ${startDateObj.toLocaleDateString()}`;
        hasChanges = true;
      }

      // Handle end date update
      if (newEndDate && getDateValue(task.endDate) !== newEndDate) {
        const endDateObj = new Date(newEndDate);
        updates.endDate = endDateObj;
        updates.deadline = endDateObj; // Keep deadline in sync
        updates.previousEndDate = task.endDate;
        dateChangeMessage += `\n• End date: ${formatDateDisplay(task.endDate)} → ${endDateObj.toLocaleDateString()}`;
        hasChanges = true;
      }

      if (!hasChanges) {
        alert('No changes detected. Please modify the dates or close the modal.');
        setIsLoading(false);
        return;
      }

      // Add metadata
      updates.extendedAt = new Date();
      updates.extendedBy = currentUser.uid;
      updates.extensionReason = reason;
      updates.updatedAt = new Date();
      
      // If only start date changed, don't override extension-specific fields
      if (!updates.previousEndDate && updates.endDate) {
        updates.previousEndDate = task.endDate;
      }

      await updateDoc(doc(db, 'tasks', task.id), updates);

      // Notify the assigned worker
      if (task.assignedTo) {
        const notificationMessage = dateChangeMessage 
          ? `Dates changed for "${task.title}":${dateChangeMessage}\nReason: ${reason}`
          : `"${task.title}" dates have been updated. Reason: ${reason}`;
        
        await addDoc(collection(db, 'notifications'), {
          userId: task.assignedTo,
          type: updates.endDate ? 'task_extended' : 'task_dates_updated',
          title: updates.endDate ? '⏰ Task Deadline Extended' : '📅 Task Dates Updated',
          message: notificationMessage,
          taskId: task.id,
          projectId: task.projectId,
          read: false,
          createdAt: new Date()
        });
      }

      alert('✅ Task dates updated successfully!');
      onSuccess?.();
      onClose();
    } catch (error) {
      console.error('Error updating task dates:', error);
      alert('Failed to update task dates');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" style={{ maxWidth: '500px' }} onClick={(e) => e.stopPropagation()}>
        <h3>📅 Edit Task Dates</h3>
        <p><strong>Task:</strong> {task.title}</p>
        <p><strong>Project:</strong> {task.projectName}</p>
        
        {/* Start Date Field */}
        <label style={{ marginTop: '10px', display: 'block' }}>
          Start Date
        </label>
        <p style={{ fontSize: '14px', color: '#666', margin: '5px 0' }}>
          Current: {formatDateDisplay(task.startDate)}
        </p>
        <input 
          type="date" 
          value={newStartDate}
          onChange={(e) => setNewStartDate(e.target.value)}
          style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #ddd', marginTop: '5px' }}
        />
        
        {/* End Date Field */}
        <label style={{ marginTop: '15px', display: 'block' }}>
          End Date *
        </label>
        <p style={{ fontSize: '14px', color: '#666', margin: '5px 0' }}>
          Current: {formatDateDisplay(task.endDate)}
        </p>
        <input 
          type="date" 
          value={newEndDate}
          onChange={(e) => setNewEndDate(e.target.value)}
          min={newStartDate || new Date().toISOString().split('T')[0]}
          style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #ddd', marginTop: '5px' }}
        />
        
        {/* Reason Field */}
        <label style={{ marginTop: '15px', display: 'block' }}>
          Reason for Change *
        </label>
        <textarea 
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="e.g., Client requested changes, Resource constraints, Scope adjustment, etc."
          rows="3"
          style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #ddd', marginTop: '8px' }}
        />
        
        {/* Actions */}
        <div className="modal-actions" style={{ marginTop: '20px' }}>
          <button onClick={handleSubmit} className="assign-btn" disabled={isLoading}>
            {isLoading ? 'Updating...' : '✓ Update Dates'}
          </button>
          <button onClick={onClose} className="cancel-btn" disabled={isLoading}>
            Cancel
          </button>
        </div>
        
        <small style={{ display: 'block', marginTop: '15px', color: '#666', fontSize: '12px' }}>
          * End date is required. You can update either or both dates.
        </small>
      </div>
    </div>
  );
};

export default ExtendTaskDateModal;